import React, { useState, useEffect } from 'react';
import { Brain, Play, LogIn } from 'lucide-react';
import Ranking from './Ranking';
import { useAuth } from '../contexts/AuthContext';
import { getRanking } from '../utils/supabase';

const StartScreen = ({ onStart, isAuthenticated = false, onLogin, onLoginSuccess }) => {
  const [rankingUsers, setRankingUsers] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [rememberLogin, setRememberLogin] = useState(true);
  const { login, loading: authLoading } = useAuth();

  useEffect(() => {
    loadRanking();
  }, []);

  const loadRanking = async () => {
    const { data } = await getRanking(10);
    if (data) {
      setRankingUsers(data);
    }
  };

  const handlePlayClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
    } else {
      onStart();
    }
  };

  const handleLogin = async () => {
    if (login) {
      const { error } = await login({ remember: rememberLogin });
      if (!error) {
        setShowLoginPrompt(false);
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          onStart();
        }
      } else {
        console.error('Error al iniciar sesión:', error);
        alert('Error al iniciar sesión. Por favor intenta de nuevo.');
      }
    } else {
      // Fallback: permitir jugar sin autenticación
      setShowLoginPrompt(false);
      onStart();
    }
  };

  const handlePlayWithoutLogin = () => {
    setShowLoginPrompt(false);
    onStart();
  };

  return (
    <div className="min-h-screen py-4 px-3 sm:py-6 sm:px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 sm:p-4 bg-white/90 rounded-3xl shadow-xl">
              <Brain className="w-10 h-10 sm:w-16 sm:h-16 text-purple-400" />
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-700 drop-shadow-sm">
              Quiz de Anatomía
            </h1>
          </div>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 font-medium">
            Pon a prueba tus conocimientos médicos
          </p>
        </div>

        {/* Ranking */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 mb-6 border border-purple-100">
          <Ranking users={rankingUsers} />
        </div>

        {/* Login Prompt Modal */}
        {showLoginPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform animate-in">
              <div className="text-center mb-6">
                <div className="inline-flex p-3 sm:p-4 bg-purple-100 rounded-full mb-4">
                  <LogIn className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                  ¡Inicia Sesión!
                </h2>
                <p className="text-sm sm:text-base text-gray-600">
                  Inicia sesión con Google para guardar tu progreso y competir en el ranking
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleLogin}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg shadow-lg border-2 border-gray-200 hover:border-purple-300 transition-all"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar con Google
                </button>

                <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={rememberLogin}
                    onChange={(e) => setRememberLogin(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span>Recordar inicio de sesión con Google en este dispositivo</span>
                </label>

                <button
                  onClick={handlePlayWithoutLogin}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  Jugar sin iniciar sesión
                </button>

                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 font-medium transition-all"
                >
                  Cancelar
                </button>
              </div>

              <p className="mt-4 text-xs text-center text-gray-500">
                Al iniciar sesión, aceptas guardar tu progreso y participar en el ranking
              </p>
            </div>
          </div>
        )}

        {/* Play Button */}
        <div className="text-center">
          <button
            onClick={handlePlayClick}
            className="group relative inline-flex items-center gap-3 sm:gap-4 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white px-8 sm:px-12 py-4 sm:py-6 rounded-2xl sm:rounded-3xl font-bold text-lg sm:text-xl md:text-2xl shadow-2xl hover:shadow-purple-500/50 transition-all transform hover:scale-105 active:scale-95 border-4 border-white/30"
          >
            <Play className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 fill-white animate-pulse" />
            <span className="relative">
              ¡Jugar!
              <span className="absolute -bottom-1 left-0 w-0 h-1 bg-white transition-all group-hover:w-full"></span>
            </span>
          </button>

          {!isAuthenticated && (
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500">
              💡 Inicia sesión para guardar tu progreso
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StartScreen;
